import { useState, useEffect, useRef } from 'react';
import { getFirestore, collection, query, where, onSnapshot } from 'firebase/firestore';

// useProcessingLeads — real-time list of in-flight leads (status: queued |
// processing) for the Activity Rail's "Processing now" section.
//
// Uses onSnapshot so new leads appear the moment they're written to Firestore
// rather than waiting for the next 30s poll. Safe to subscribe here because
// the in-flight set is always tiny (0–5 docs); no payload risk.
//
// Mirrors the stuck-lead cutoff from useInProgressLeadsCount so a crashed
// worker doesn't strand a lead in the section forever.
export function useProcessingLeads(companyId) {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const snapshotRef = useRef([]);

    useEffect(() => {
        if (!companyId) {
            setLeads([]);
            setLoading(false);
            snapshotRef.current = [];
            return undefined;
        }

        setLoading(true);
        const db = getFirestore();
        const q = query(
            collection(db, `companies/${companyId}/leads`),
            where('data.status', 'in', ['queued', 'processing'])
        );

        const recompute = () => {
            const cutoff = Date.now() - STUCK_THRESHOLD_MS;
            const active = snapshotRef.current
                .filter((lead) => {
                    const ms = toMillis(lead.data?.createdAt);
                    return ms !== null && ms >= cutoff;
                })
                .sort((a, b) => {
                    const aMs = toMillis(a.data?.createdAt) || 0;
                    const bMs = toMillis(b.data?.createdAt) || 0;
                    return bMs - aMs;
                });
            setLeads(active);
        };

        let cancelled = false;
        const unsubscribe = onSnapshot(
            q,
            (snap) => {
                if (cancelled) return;
                const next = [];
                snap.forEach((docSnap) => {
                    const d = docSnap.data();
                    next.push({
                        leadId: d?.data?.leadId || docSnap.id,
                        propertyId: d?.data?.propertyId || null,
                        data: d?.data || {}
                    });
                });
                snapshotRef.current = next;
                recompute();
                setLoading(false);
            },
            (err) => {
                if (cancelled) return;
                console.error('useProcessingLeads snapshot error:', err);
                snapshotRef.current = [];
                setLeads([]);
                setLoading(false);
            }
        );

        const ageOutInterval = setInterval(() => {
            if (!cancelled) recompute();
        }, 30 * 1000);

        return () => {
            cancelled = true;
            unsubscribe();
            clearInterval(ageOutInterval);
        };
    }, [companyId]);

    return { leads, loading };
}

// Anything older than this is treated as a stuck worker / dead pipeline, not
// "still processing." Enrichment normally completes in <60s; retries can push
// it to a couple of minutes. 15 minutes is well beyond normal.
const STUCK_THRESHOLD_MS = 15 * 60 * 1000;

function toMillis(timestamp) {
    if (!timestamp) return null;
    if (typeof timestamp.toMillis === 'function') return timestamp.toMillis();
    if (typeof timestamp.seconds === 'number') return timestamp.seconds * 1000;
    const parsed = new Date(timestamp).getTime();
    return Number.isFinite(parsed) ? parsed : null;
}

// Live count of leads currently working their way through propertyIngestionV2
// (status: queued | processing) AND created within the last 15 minutes.
// Caller doesn't need to refresh — Firestore pushes deltas as workers flip
// statuses to 'complete' or 'failed'.
//
// The createdAt cutoff is applied client-side rather than as a second `where()`
// clause, which avoids needing a composite Firestore index. Stuck leads (worker
// crashed, retries exhausted with status never flipped) are excluded from the
// count rather than misleading the user with "Processing 7 new leads" forever.
//
// A 30s interval re-filters the cached snapshot so a lead that ages past the
// cutoff without any Firestore write eventually rolls out of the count too.
export function useInProgressLeadsCount(companyId) {
    const [count, setCount] = useState(0);
    const createdAtsRef = useRef([]);

    useEffect(() => {
        if (!companyId) {
            setCount(0);
            createdAtsRef.current = [];
            return undefined;
        }

        const db = getFirestore();
        const q = query(
            collection(db, `companies/${companyId}/leads`),
            where('data.status', 'in', ['queued', 'processing'])
        );

        let cancelled = false;
        const recompute = () => {
            const cutoff = Date.now() - STUCK_THRESHOLD_MS;
            const inFlight = createdAtsRef.current.filter((ms) => ms !== null && ms >= cutoff).length;
            setCount(inFlight);
        };

        const unsubscribe = onSnapshot(
            q,
            (snap) => {
                if (cancelled) return;
                const next = [];
                snap.forEach((doc) => next.push(toMillis(doc.data()?.data?.createdAt)));
                createdAtsRef.current = next;
                recompute();
            },
            (err) => {
                if (cancelled) return;
                console.error('useInProgressLeadsCount snapshot error:', err);
                createdAtsRef.current = [];
                setCount(0);
            }
        );

        const ageOutInterval = setInterval(() => {
            if (cancelled) return;
            recompute();
        }, 30 * 1000);

        return () => {
            cancelled = true;
            unsubscribe();
            clearInterval(ageOutInterval);
        };
    }, [companyId]);

    return count;
}
